import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Plus, Search, KeyRound, Ban, CheckCircle2, Edit, Loader2, Plane, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const emptyForm = {
  nome: "", cpf: "", matricula: "", telefone: "", email: "",
  veiculo: "", placa: "", rota: "", supervisor: "", faz_rota_ar: false, senha_temporaria: "trocar123",
};

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/drivers");
      setDrivers(data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const filtered = drivers.filter((d) => {
    const q = query.toLowerCase();
    return !q || d.nome.toLowerCase().includes(q) || d.cpf.includes(q) || (d.matricula || "").toLowerCase().includes(q);
  });

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  }
  function openEdit(d) {
    setEditing(d);
    setForm({ ...emptyForm, ...d });
    setOpen(true);
  }

  async function save() {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/drivers/${editing.id}`, form);
        toast.success("Motorista atualizado");
      } else {
        await api.post("/drivers", form);
        toast.success("Motorista cadastrado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function block(d) {
    try {
      if (d.status === "ativo") {
        await api.post(`/drivers/${d.id}/block`);
        toast.success(`${d.nome} bloqueado`);
      } else {
        await api.post(`/drivers/${d.id}/unblock`);
        toast.success(`${d.nome} reativado`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function resetPwd(d) {
    try {
      const r = await api.post(`/drivers/${d.id}/reset-password`);
      toast.success(`Senha redefinida para: ${r.data.temp_password}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function toggleVacation(d) {
    const goingOnVacation = d.status !== "ferias";
    const message = goingOnVacation
      ? `Colocar ${d.nome} de férias? O acesso será suspenso até a reativação.`
      : `Retirar ${d.nome} de férias e reativar o acesso?`;
    if (!window.confirm(message)) return;
    try {
      await api.put(`/drivers/${d.id}`, { status: goingOnVacation ? "ferias" : "ativo" });
      toast.success(goingOnVacation ? "Motorista colocado de férias" : "Motorista reativado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function removeDriver(d) {
    if (!window.confirm(`Apagar o cadastro de ${d.nome}? O histórico de fechamentos será preservado.`)) return;
    try {
      await api.delete(`/drivers/${d.id}`);
      toast.success("Cadastro do motorista desligado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">Equipe</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Motoristas</h1>
          <p className="mt-1 text-sm text-slate-500">{drivers.length} cadastrados · {drivers.filter(d => d.status === "ativo").length} ativos</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-blue-900 hover:bg-blue-800" data-testid="drivers-add-btn">
              <Plus className="mr-1.5 h-4 w-4" /> Novo motorista
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar motorista" : "Cadastrar motorista"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Nome" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} testId="form-nome" full />
              <Field label="CPF" value={form.cpf} onChange={(v) => setForm({ ...form, cpf: v })} testId="form-cpf" />
              <Field label="Matrícula" value={form.matricula} onChange={(v) => setForm({ ...form, matricula: v })} testId="form-matricula" />
              <Field label="Telefone" value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v })} testId="form-telefone" />
              <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="form-email" />
              <Field label="Veículo" value={form.veiculo} onChange={(v) => setForm({ ...form, veiculo: v })} testId="form-veiculo" />
              <Field label="Placa" value={form.placa} onChange={(v) => setForm({ ...form, placa: v })} testId="form-placa" />
              <Field label="Rota" value={form.rota} onChange={(v) => setForm({ ...form, rota: v })} testId="form-rota" />
              <Field label="Supervisor" value={form.supervisor} onChange={(v) => setForm({ ...form, supervisor: v })} testId="form-supervisor" />
              <label className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium">
                <input type="checkbox" checked={!!form.faz_rota_ar} onChange={(e) => setForm({ ...form, faz_rota_ar: e.target.checked })} data-testid="form-faz-rota-ar" />
                Faz rota AR
              </label>
              {!editing && (
                <Field label="Senha temporária" value={form.senha_temporaria} onChange={(v) => setForm({ ...form, senha_temporaria: v })} testId="form-senha" />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="form-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 p-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar por nome, CPF ou matrícula"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
              data-testid="drivers-search-input"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>CPF</TableHead>
                <TableHead>Matrícula</TableHead>
                <TableHead>Rota</TableHead>
                <TableHead>Placa</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="py-10 text-center text-slate-400">Carregando…</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-10 text-center text-slate-400">Nenhum motorista.</TableCell></TableRow>
              )}
              {filtered.map((d) => (
                <TableRow key={d.id} data-testid={`driver-row-${d.id}`}>
                  <TableCell className="font-medium text-slate-900">{d.nome}</TableCell>
                  <TableCell className="font-mono text-slate-600">{d.cpf}</TableCell>
                  <TableCell>{d.matricula}</TableCell>
                  <TableCell>{d.rota || "—"}</TableCell>
                  <TableCell>{d.placa || "—"}</TableCell>
                  <TableCell>
                    <Badge className={d.status === "ativo" ? "bg-green-100 text-green-800 border-green-200" : d.status === "ferias" ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-slate-200 text-slate-700"}>
                      {d.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <IconBtn onClick={() => openEdit(d)} title="Editar" data-testid={`driver-edit-${d.id}`}><Edit className="h-4 w-4" /></IconBtn>
                      <IconBtn onClick={() => resetPwd(d)} title="Redefinir senha" data-testid={`driver-reset-${d.id}`}><KeyRound className="h-4 w-4" /></IconBtn>
                      <IconBtn onClick={() => toggleVacation(d)} title={d.status === "ferias" ? "Retirar de férias" : "Colocar de férias"} data-testid={`driver-vacation-${d.id}`}>
                        <Plane className={`h-4 w-4 ${d.status === "ferias" ? "text-blue-700" : "text-slate-600"}`} />
                      </IconBtn>
                      <IconBtn onClick={() => block(d)} title={d.status === "ativo" ? "Bloquear" : "Reativar"} data-testid={`driver-block-${d.id}`}>
                        {d.status === "ativo" ? <Ban className="h-4 w-4 text-red-600" /> : <CheckCircle2 className="h-4 w-4 text-green-600" />}
                      </IconBtn>
                      <IconBtn onClick={() => removeDriver(d)} title="Apagar cadastro" data-testid={`driver-delete-${d.id}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </IconBtn>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, full, testId }) {
  return (
    <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

function IconBtn({ children, ...rest }) {
  return (
    <button
      {...rest}
      className="rounded-md border border-slate-200 bg-white p-2 text-slate-600 transition hover:border-blue-700 hover:text-blue-700"
    >
      {children}
    </button>
  );
}
